// only FilterXSS: xss is CommonJS and Node's ESM loader cannot see its other exports (set in a loop),
// importing one of them breaks the production server bundle (it worked with Vite in dev)
import { FilterXSS } from 'xss'

import { escapeHtmlAttribute, safeEmbedUrl } from './helpers'

/**
 * Defence in depth for the HTML written by Wapers (posts, comments, reviews) and rendered with v-html.
 * The API already refuses anything outside this list (BaseController::hasOnlyHtmlTagsAllowed and co),
 * this filter makes sure older content or a gap in those checks can never run in the page.
 * js-xss parses without a DOM, so it runs the same way during SSR and in the browser.
 *
 * Allowed: the tags of the API list, the attributes of mentions and of the links added by linkify(),
 * and the <img>/<iframe> the front builds itself from contentImages/contentEmbed.
 */
// attribute values reach onTagAttr as written in the HTML: decode before checking and escaping again
const decodeAttribute = (value: string) =>
  value
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')

const userHtmlFilter = new FilterXSS({
  whiteList: {
    p: [],
    br: [],
    strong: [],
    em: [],
    u: [],
    ul: [],
    li: [],
    a: [
      'href',
      'class',
      'data-mention-id',
      'data-user-id',
      'data-user-slug',
      'target',
      'rel',
    ],
    img: ['src', 'alt', 'loading'],
    iframe: [
      'src',
      'frameborder',
      'allowfullscreen',
      'contenteditable',
      'draggable',
      'loading',
    ],
  },
  // unknown tags are removed but their text is kept, the content of these ones is dropped
  stripIgnoreTag: true,
  stripIgnoreTagBody: ['script', 'style', 'noscript', 'template'],
  allowCommentTag: false,
  css: false,
  // src of the img/iframe the front builds: own rules; any other attribute (href…) falls back on the
  // js-xss default check: http(s), mailto, tel, relative paths and anchors only, entities and control
  // characters decoded first ("jav&#x61;script:" is caught)
  onTagAttr: (tag, name, value) => {
    if (tag === 'iframe' && name === 'src') {
      return `src="${escapeHtmlAttribute(safeEmbedUrl(decodeAttribute(value)) ?? '')}"`
    }
    if (tag === 'img' && name === 'src') {
      const src = decodeAttribute(value).trim()
      const safe = /^(https:\/\/|\/(?!\/))/i.test(src) ? src : ''
      return `src="${escapeHtmlAttribute(safe)}"`
    }
    return undefined
  },
})

export const sanitizeUserHtml = (html?: string | null) =>
  html ? userHtmlFilter.process(html) : ''

/**
 * Reviews are plain text (Bazaarvoice): escape them instead of filtering them as HTML,
 * so a "<3" or "->" written by a Waper is shown as typed.
 */
export const userTextToHtml = (text?: string | null) =>
  escapeHtmlAttribute(text ?? '')
